'use client';

import * as React from 'react';

import { cn } from '@/lib/ui-kit/utils';

interface TabItem<T extends string = string> {
  value: T;
  label: React.ReactNode;
}

interface TabGroupProps<T extends string = string> {
  items: TabItem<T>[];
  value: T;
  onValueChange: (value: T) => void;
  className?: string;
  /**
   * When true, the tab row is horizontally scrollable. The overflowing edges are
   * masked out so the next/previous chip visibly dissolves instead of being cut
   * off — that partial chip is the cue that there is more to scroll. The active
   * tab is also kept fully inside the visible window.
   */
  scrollable?: boolean;
  /**
   * Extra classes for the sliding active-tab fill. The default fill is the
   * secondary button colour, which vanishes on a panel painted the same.
   */
  indicatorClassName?: string;
}

/** Width of the edge fade, in px. Grows with the scroll offset up to this value. */
const FADE_WIDTH = 40;

function TabGroup<T extends string = string>({
  items,
  value,
  onValueChange,
  className,
  scrollable = false,
  indicatorClassName,
}: TabGroupProps<T>) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = React.useState<{ left: number; width: number } | null>(null);
  const [animated, setAnimated] = React.useState(false);
  // Mirror of `animated` for the scroll effect — reading it through a ref keeps
  // it out of that effect's dependency list, so flipping it can't re-scroll.
  const animatedRef = React.useRef(false);

  // Recalculate indicator position when active tab changes, and pull the tab
  // into view when it sits (even partially) outside the scroll window.
  React.useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const activeEl = container.querySelector<HTMLElement>(`[data-value="${CSS.escape(value)}"]`);
    if (!activeEl) return;
    setIndicator({
      left: activeEl.offsetLeft,
      width: activeEl.offsetWidth,
    });

    const scroller = scrollRef.current;
    if (!scrollable || !scroller) return;
    // Keep the fade width clear on both sides so the active tab never lands
    // under the mask and reads as half-faded.
    const tabStart = activeEl.offsetLeft - FADE_WIDTH;
    const tabEnd = activeEl.offsetLeft + activeEl.offsetWidth + FADE_WIDTH;
    const viewStart = scroller.scrollLeft;
    const viewEnd = viewStart + scroller.clientWidth;

    let target = viewStart;
    if (tabStart < viewStart) target = tabStart;
    else if (tabEnd > viewEnd) target = tabEnd - scroller.clientWidth;
    target = Math.max(0, Math.min(target, scroller.scrollWidth - scroller.clientWidth));

    if (Math.abs(target - viewStart) > 1) {
      // Jump on the first paint (initial tag from the URL), animate afterwards.
      scroller.scrollTo({ left: target, behavior: animatedRef.current ? 'smooth' : 'auto' });
    }
  }, [value, scrollable]);

  // Enable transitions only after the first paint so initial position does not animate in
  React.useEffect(() => {
    if (indicator && !animated) {
      const id = requestAnimationFrame(() => {
        animatedRef.current = true;
        setAnimated(true);
      });
      return () => cancelAnimationFrame(id);
    }
  }, [indicator, animated]);

  // Drive the edge fades straight from the scroll offset: each side grows from
  // 0 to FADE_WIDTH as soon as there is content hidden behind it. Written to CSS
  // vars instead of state so scrolling doesn't re-render the whole tab row.
  React.useEffect(() => {
    if (!scrollable) return;
    const el = scrollRef.current;
    const content = containerRef.current;
    if (!el) return;

    const update = () => {
      const scrollableX = Math.max(0, el.scrollWidth - el.clientWidth);
      const start = Math.max(0, Math.min(FADE_WIDTH, el.scrollLeft));
      const end = Math.max(0, Math.min(FADE_WIDTH, scrollableX - el.scrollLeft));
      el.style.setProperty('--tab-fade-start', `${start}px`);
      el.style.setProperty('--tab-fade-end', `${end}px`);
    };

    update();
    el.addEventListener('scroll', update, { passive: true });

    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (content) ro.observe(content);

    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [scrollable]);

  const tabList = (
    <div
      ref={containerRef}
      role="tablist"
      className={cn('relative inline-flex items-center gap-1', className)}
    >
      {/* Sliding background indicator */}
      {indicator && (
        <div
          aria-hidden
          className={cn(
            'pointer-events-none absolute top-0 left-0 h-[32px] rounded-[10px] bg-v2-btn-secondary shadow-[0px_0px_20px_0px_rgba(0,41,98,0.1)]',
            indicatorClassName,
          )}
          style={{
            transform: `translateX(${indicator.left}px)`,
            width: indicator.width,
            transition: animated
              ? 'transform 250ms ease-in-out, width 250ms ease-in-out'
              : undefined,
          }}
        />
      )}

      {items.map((item) => (
        <Tab
          key={item.value}
          value={item.value}
          active={item.value === value}
          onClick={() => onValueChange(item.value)}
        >
          {item.label}
        </Tab>
      ))}
    </div>
  );

  if (!scrollable) return tabList;

  // Masking the content (rather than overlaying a background-coloured gradient)
  // keeps the effect correct on any surface and in both themes.
  const edgeMask =
    'linear-gradient(to right, transparent 0, #000 var(--tab-fade-start, 0px), #000 calc(100% - var(--tab-fade-end, 0px)), transparent 100%)';

  return (
    <div
      ref={scrollRef}
      className="scrollbar-hide min-w-0 flex-1 overflow-x-auto overflow-y-hidden"
      style={{ maskImage: edgeMask, WebkitMaskImage: edgeMask }}
    >
      {tabList}
    </div>
  );
}

interface TabProps {
  value: string;
  active?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
}

function Tab({ value, active, onClick, children, className }: TabProps) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-value={value}
      onClick={onClick}
      className={cn(
        'relative z-10 flex h-[32px] cursor-pointer items-center justify-center rounded-[10px] px-4 py-2 outline-none',
        'focus-visible:ring-2 focus-visible:ring-v2-bg-blue-50',
        'text-[13px] font-normal leading-4 tracking-[-0.13px] whitespace-nowrap',
        'transition-colors duration-150',
        active ? 'text-v2-text-primary' : 'text-v2-text-secondary hover:text-v2-text-primary',
        className,
      )}
    >
      {children}
    </button>
  );
}

export type { TabGroupProps, TabItem, TabProps };
export { Tab, TabGroup };
